import { Service, TimeSlotsHook } from "@models";
import i18n from "../../i18n";
import { DEFAULT_TIME_ZONE } from "@shared";
import { useStore } from "@stores";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo } from "react";

// values kept as-is since they are persisted to the backend
const getNotAutoAvailableSlots = () => [
  { label: i18n.t("MorningSlot"), value: "Prepodne" },
  { label: i18n.t("AfternoonSlot"), value: "Poslepodne" },
  { label: i18n.t("AnyTimeSlot"), value: "Svejedno" },
];

interface IProps {
  workerId: string | number | undefined;
  tenantId: string | undefined;
  date: Dayjs | null;
  selectedServices: Map<number, Service>;
}

export const useTenantAvailableTimeSlots = ({
  workerId,
  tenantId,
  date,
  selectedServices,
}: IProps): TimeSlotsHook => {
  const { tenantStore } = useStore();
  const { availableTimeSlotsLoading, availableTimeSlots } = tenantStore;

  const requiredMinutes = useMemo(() => {
    return Array.from(selectedServices?.values())
      .filter((x) => x.isAutoAvailable)
      .reduce((sum, s) => sum + (s.duration || 0), 0);
  }, [selectedServices]);

  useEffect(() => {
    if (!workerId || !tenantId || !date || requiredMinutes <= 0) return;

    tenantStore.getAvailableSlots({
      date: dayjs(date).format(),
      userId: workerId,
      tenantId,
      requiredMinutes,
    });

    return () => tenantStore.clearTimeSlots();
  }, [tenantStore, workerId, tenantId, date, requiredMinutes]);

  const allowAutoAvailable = useMemo(() => {
    if (selectedServices.size === 0 || !date || !workerId) return false;
    return Array.from(selectedServices.values()).every((x) => x.isAutoAvailable);
  }, [selectedServices, date, workerId]);

  const timeOptions = useMemo(() => {
    if (!allowAutoAvailable) return getNotAutoAvailableSlots();

    const zone = tenantStore.tenant?.timeZone || DEFAULT_TIME_ZONE;

    return availableTimeSlots
      .filter((x: string) => {
        if (!date) return true;
        return dayjs.tz(`${date.format("YYYY-MM-DD")} ${x}`, zone).isAfter(dayjs());
      })
      .map((x: string) => ({ label: x.substring(0, 5), value: x.substring(0, 5) }));
  }, [availableTimeSlots, allowAutoAvailable, date, tenantStore.tenant?.timeZone]);

  const noTimeSlotsAvailable = !availableTimeSlotsLoading && timeOptions.length === 0;

  return {
    dataSource: availableTimeSlots,
    loading: availableTimeSlotsLoading,
    allowAutoAvailable,
    timeOptions,
    noTimeSlotsAvailable,
  };
};
